package domain.usecase

import domain.model.OrganizationAccessDiagnosis
import domain.repository.GitHubInsightsRepository

/**
 * Use case for diagnosing organization access issues
 */
class DiagnoseOrganizationAccessUseCase(
    private val repository: GitHubInsightsRepository
) {
    
    /**
     * Diagnose why user might not be seeing all their organizations
     */
    suspend fun execute(): Result<OrganizationAccessDiagnosis> {
        return repository.diagnoseOrganizationAccess()
    }
    
    /**
     * Get detailed steps to resolve organization access issues
     */
    fun getOrganizationAccessSteps(): List<String> {
        return listOf(
            "🔍 **Check OAuth Application Settings**",
            "   → Visit: https://github.com/settings/applications",
            "   → Look for this application and verify permissions",
            "",
            "🏢 **Organization Settings to Check**",
            "   → Go to Organization Settings > Third-party access",
            "   → Look for 'OAuth application policy'",
            "   → Ensure policy allows or whitelist this app",
            "",
            "👥 **Contact Organization Admins**",
            "   → Ask them to approve this OAuth application",
            "   → They can do this in org settings > Third-party access",
            "",
            "🔐 **Re-authorize with Correct Scopes**",
            "   → The app needs 'read:org' scope for organizations",
            "   → 'repo' scope for private repositories",
            "   → 'user:email' for email verification",
            "",
            "🔄 **SAML SSO (if applicable)**",
            "   → Some orgs require SAML SSO completion",
            "   → Complete SSO flow if prompted",
            "",
            "⚠️ **Common Issues**",
            "   → Organization has restricted third-party apps",
            "   → OAuth app missing required scopes",
            "   → User needs to complete SSO authentication",
            "   → Organization admin hasn't approved the app"
        )
    }
    
    /**
     * Generate OAuth authorization URL with correct scopes
     */
    fun generateReauthorizationUrl(
        clientId: String,
        redirectUri: String
    ): String {
        val scopes = listOf(
            "read:user",
            "user:email", 
            "read:org",
            "repo",
            "workflow"
        ).joinToString(",")
        
        return "https://github.com/login/oauth/authorize?" +
                "client_id=$clientId" +
                "&redirect_uri=$redirectUri" +
                "&scope=$scopes" +
                "&state=reauth_for_orgs" +
                "&allow_signup=false"
    }
}